import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  AccountCircle as UserIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import FinancialControlService, {
  FinancialProject,
  FinancialDashboard,
} from '../../services/financialControlService';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

const COLORS = {
  pix: '#1976D2',
  card: '#42A5F5',
  boleto: '#FFB74D',
  outros: '#EF5350',
};

const FinancialControlDashboard: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const [projects, setProjects] = useState<FinancialProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('last_7_days');
  const [dashboard, setDashboard] = useState<FinancialDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [expandedMetrics, setExpandedMetrics] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      loadDashboard();
    }
  }, [selectedProject, dateRange, selectedAccount, selectedPlatform, selectedProduct, projects]);

  const loadProjects = async () => {
    try {
      const response = await FinancialControlService.listProjects();
      if (response.success && response.data) {
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const startDate = getStartDateFromRange(dateRange);
      const endDate = format(new Date(), 'yyyy-MM-dd');

      const params: any = {
        start_date: startDate,
        end_date: endDate,
      };
      if (selectedProject !== 'all') {
        params.project_id = selectedProject;
      }
      if (selectedAccount !== 'all') {
        params.account_id = selectedAccount;
      }
      if (selectedPlatform !== 'all') {
        params.platform = selectedPlatform;
      }
      if (selectedProduct !== 'all') {
        params.product = selectedProduct;
      }

      const response = await FinancialControlService.getDashboard(params);
      if (response.success && response.data) {
        setDashboard(response.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDateFromRange = (range: string): string => {
    const today = new Date();
    switch (range) {
      case 'last_7_days':
        return format(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
      case 'last_30_days':
        return format(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
      case 'last_90_days':
        return format(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
      default:
        return format(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatTimeAgo = (date: Date) => {
    try {
      return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
    } catch {
      return 'agora mesmo';
    }
  };

  // Calcular métricas adicionais
  const metrics = React.useMemo(() => {
    if (!dashboard) {
      return {
        faturamento_liquido: 0,
        gastos_anuncios: 0,
        roas: 0,
        lucro: 0,
        vendas_pendentes: 0,
        roi: 0,
        vendas_reembolsadas: 0,
        margem_lucro: 0,
        imposto: 0,
        reembolso_percent: 0,
        chargeback_percent: 0,
        arpu: 0,
        total_vendas: 0,
      };
    }

    const m = dashboard.metrics;
    
    // Usar valores já calculados do backend quando disponíveis
    const faturamentoLiquido = m.faturamento_liquido || 0;
    const gastosAnuncios = m.gastos_anuncios || 0;
    const roas = m.roas || 0;
    const lucro = m.lucro || 0;
    const totalPending = m.vendas_pendentes || 0;
    const roi = m.roi || 0;
    const totalRefunded = m.vendas_reembolsadas || 0;
    const margemLucro = m.margem_lucro || 0;
    const totalTax = m.imposto || 0;
    const reembolsoPercent = m.reembolso_percent || 0;
    const totalChargeback = 0; // m.chargeback não disponível ainda
    const chargebackPercent = m.chargeback_percent || 0;
    const totalSales = m.total_vendas || 0;
    const arpu = m.arpu || 0;

    return {
      faturamento_liquido: faturamentoLiquido,
      gastos_anuncios: gastosAnuncios,
      roas: roas,
      lucro: lucro,
      vendas_pendentes: totalPending,
      roi: roi,
      vendas_reembolsadas: totalRefunded,
      margem_lucro: margemLucro,
      imposto: totalTax,
      reembolso_percent: reembolsoPercent,
      chargeback_percent: chargebackPercent,
      arpu: arpu,
      total_vendas: totalSales,
    };
  }, [dashboard]);

  // Preparar dados para gráfico de vendas por pagamento
  const salesByPaymentData = React.useMemo(() => {
    if (!dashboard?.revenue_by_payment_method) return [];

    const total = dashboard.revenue_by_payment_method.reduce((sum, item) => sum + item.count, 0);
    
    return dashboard.revenue_by_payment_method.map((item) => {
      const methodName = item.payment_method === 'credit_card' || item.payment_method === 'card' ? 'Cartão' : 
            item.payment_method === 'pix' ? 'Pix' :
            item.payment_method === 'boleto' ? 'Boleto' : 'Outros';
      const colorKey = item.payment_method === 'credit_card' || item.payment_method === 'card' ? 'card' :
            item.payment_method === 'pix' ? 'pix' :
            item.payment_method === 'boleto' ? 'boleto' : 'outros';
      
      return {
        name: methodName,
        value: item.count,
        percent: total > 0 ? parseFloat(((item.count / total) * 100).toFixed(0)) : 0,
        color: COLORS[colorKey as keyof typeof COLORS] || COLORS.outros,
      };
    });
  }, [dashboard]);

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
            Dashboard - Principal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right', mr: 2 }}>
            {user && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                  {user.full_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.role === 'admin' ? 'Administrador' : 'Agente'}
                </Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Atualizado há {formatTimeAgo(lastUpdate)}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <UserIcon />
            )}
          </Avatar>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={loadDashboard} 
            disabled={loading}
            sx={{ ml: 2 }}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* Filtros - Seção Resumo */}
      <Card sx={{ mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            Resumo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Data de cadastro</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Data de cadastro"
                >
                  <MenuItem value="last_7_days">Últimos 7 dias</MenuItem>
                  <MenuItem value="last_30_days">Últimos 30 dias</MenuItem>
                  <MenuItem value="last_90_days">Últimos 90 dias</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Conta de Anúncio</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  label="Conta de Anúncio"
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {/* Adicionar contas do Facebook Ads aqui */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Plataforma</InputLabel>
                <Select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  label="Plataforma"
                >
                  <MenuItem value="all">Qualquer</MenuItem>
                  <MenuItem value="kirvano">Kirvano</MenuItem>
                  <MenuItem value="hotmart">Hotmart</MenuItem>
                  <MenuItem value="kiwify">Kiwify</MenuItem>
                  <MenuItem value="braip">Braip</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Produto</InputLabel>
                <Select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  label="Produto"
                >
                  <MenuItem value="all">Qualquer</MenuItem>
                  {/* Adicionar produtos aqui */}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 4 Cards Principais */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Faturamento Líquido
                    </Typography>
                    <Tooltip title="Receita total após descontar taxas e impostos">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.faturamento_liquido)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Gastos com anúncios
                    </Typography>
                    <Tooltip title="Total gasto com publicidade">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.gastos_anuncios)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      ROAS
                    </Typography>
                    <Tooltip title="Return on Ad Spend - Retorno sobre investimento em anúncios">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {metrics.roas > 0 ? metrics.roas.toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Lucro
                    </Typography>
                    <Tooltip title="Lucro líquido após todos os custos">
                      <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: metrics.lucro >= 0 ? 'success.main' : 'error.main' }}>
                    {formatCurrency(metrics.lucro)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Segunda Linha - Gráfico e Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Gráfico de Vendas por Pagamento */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Vendas por Pagamento
                </Typography>
                <Tooltip title="Distribuição de vendas por método de pagamento">
                  <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              {salesByPaymentData.length > 0 ? (
                <Box sx={{ position: 'relative', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salesByPaymentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={(props: any) => {
                          const percent = props?.percent || 0;
                          return `${(percent * 100).toFixed(0)}%`;
                        }}
                        labelLine={false}
                      >
                        {salesByPaymentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: '8px',
                          color: theme.palette.text.primary,
                        }}
                        itemStyle={{
                          color: theme.palette.text.primary,
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value, entry: any) => (
                          <span style={{ color: theme.palette.mode === 'dark' ? theme.palette.text.primary : entry.color }}>
                            {value}: {salesByPaymentData.find(d => d.name === value)?.value || 0}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                      Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {metrics.total_vendas}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">Nenhuma venda encontrada</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cards Adicionais - 4 linhas x 2 colunas */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            {/* Linha 1 */}
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Vendas Pendentes
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.vendas_pendentes)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      ROI
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {metrics.roi.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Linha 2 */}
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Vendas Reembolsadas
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.vendas_reembolsadas)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Margem de Lucro
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {metrics.margem_lucro.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Linha 3 */}
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Imposto
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.imposto)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Reembolso
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {metrics.reembolso_percent.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Linha 4 */}
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      Chargeback
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {metrics.chargeback_percent.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      ARPU
                    </Typography>
                    <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {formatCurrency(metrics.arpu)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinancialControlDashboard;
